import React from "react";
import { useState, useEffect, useContext } from "react";
import { Link } from "react-router-dom";
import { BsRecycle } from "react-icons/bs";
import ProductQuickView from "./ProductQuickView";
import { toast } from "react-toastify";
import StarRating from "./StarRating";
import { CartContext } from "../context/CartContext";
import api from "../services/api";

const ProductCard = ({ product, onQuickView }) => {
  const { addToCart = () => {} } = React.useContext(CartContext) || {};
  const [showQuickView, setShowQuickView] = React.useState(false);
  const [selectedSize, setSelectedSize] = React.useState(null);
  const [isFavorite, setIsFavorite] = React.useState(false);

  const productId = product?._id?.$oid || product?._id;

  React.useEffect(() => {
    if (!productId) return;
    const favorites = JSON.parse(localStorage.getItem("favorites") || "[]");
    setIsFavorite(favorites.includes(productId));
  }, [productId]);

  if (!product) return null;

  // Get user from local storage
  const user = JSON.parse(localStorage.getItem("user"));



  const handleToggleFavorite = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!user) {
      toast.info("Vui lòng đăng nhập để lưu sản phẩm yêu thích");
      return;
    }

    try {
      const res = await api.post("/favorites/toggle", { productId });
      const newStatus = res.data.isFavorite;
      setIsFavorite(newStatus);

      // Update local storage cache of favorites for quick UI response
      let favorites = JSON.parse(localStorage.getItem("favorites") || "[]");
      if (newStatus) {
        if (!favorites.includes(productId)) favorites.push(productId);
      } else {
        favorites = favorites.filter((id) => id !== productId);
      }
      localStorage.setItem("favorites", JSON.stringify(favorites));

      toast.success(res.data.message);
    } catch (err) {
      console.error("Error toggling favorite:", err);
      toast.error(err.response?.data?.error || "Có lỗi xảy ra");
    }
  };

  const img =
    product.image ||
    product.images?.[0] ||
    "https://placehold.co/400x400?text=No+Image";

  const formatPrice = (price) => {
    return new Intl.NumberFormat("vi-VN").format(price);
  };

  const handleAddToCart = (e, size = null) => {
    e.stopPropagation();

    if (product.stock <= 0) {
      toast.error("Sản phẩm đã hết hàng!");
      return;
    }

    const finalSize =
      size ||
      (product.sizes && product.sizes.length > 0
        ? product.sizes[0]
        : "One Size");
    const finalColor =
      product.colors && product.colors.length > 0
        ? typeof product.colors[0] === "string"
          ? product.colors[0]
          : product.colors[0].name
        : "Mặc định";

    const payload = {
      productId: productId,
      qty: 1,
      selectedColor: finalColor,
      selectedSize: finalSize,
      price: product.price,
      name: product.name,
      image: img,
    };

    addToCart(payload);
    toast.success(
      `✅ Đã thêm ${product.name} (Size ${finalSize}) vào giỏ hàng!`,
    );
  };

  const handleQuickView = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowQuickView(true);
  };

  return (
    <>
      <div className="group relative bg-white dark:bg-slate-900 shadow-sm rounded-2xl overflow-hidden hover:shadow-2xl dark:hover:shadow-blue-900/10 hover:-translate-y-2 transition-all duration-500 border border-gray-100 dark:border-slate-800 flex flex-col h-full">
        {/* Image Container with Overlays */}
        <div className="relative aspect-[4/5] overflow-hidden bg-gray-100 dark:bg-slate-800">
          <Link
            to={`/product/${product.slug || productId}`}
            className="absolute inset-0 z-10"
          >
            <img
              src={img}
              alt={product.name || "Product"}
              className="w-full h-full object-cover transform transition-transform duration-700 group-hover:scale-110"
            />

            {/* Badges */}
            <div className="absolute top-2 left-2 sm:top-4 sm:left-4 flex flex-row flex-wrap gap-1 sm:flex-col sm:gap-2 z-20">
              {product.discount > 0 && (
                <div className="bg-red-500 text-white px-1.5 py-0.5 sm:px-3 sm:py-1 rounded-full text-[8px] sm:text-[10px] font-black shadow-lg uppercase tracking-wider">
                  -{product.discount}%
                </div>
              )}
              {product.stock <= 0 ? (
                <div className="bg-gray-800/80 text-white px-1.5 py-0.5 sm:px-3 sm:py-1 rounded-full text-[8px] sm:text-[10px] font-black shadow-lg uppercase tracking-wider backdrop-blur-sm border border-gray-600">
                  Hết hàng
                </div>
              ) : (
                product.isNew && (
                  <div className="bg-blue-600 text-white px-1.5 py-0.5 sm:px-3 sm:py-1 rounded-full text-[8px] sm:text-[10px] font-black shadow-lg uppercase tracking-wider">
                    New
                  </div>
                )
              )}
              {product.isSecondHand && (
                <div className="bg-amber-500 text-white px-1.5 py-0.5 sm:px-3 sm:py-1 rounded-full text-[8px] sm:text-[10px] font-black shadow-lg uppercase tracking-wider flex items-center gap-1 border border-white">
                  <BsRecycle size={12} className="sm:size-3" />
                  <span className="hidden xs:inline">SecondHand</span>
                  <span className="xs:hidden">SH</span>
                </div>
              )}
            </div>
          </Link>

          {/* Wishlist Button - Outside Link but inside relative container */}
          <button
            onClick={handleToggleFavorite}
            className={`absolute top-4 right-4 z-30 w-9 h-9 rounded-full flex items-center justify-center transition-all duration-300 shadow-lg ${
              isFavorite
                ? "bg-red-500 text-white scale-110"
                : "bg-white/80 dark:bg-slate-900/80 text-gray-400 hover:text-red-500 hover:scale-110 backdrop-blur-sm"
            }`}
          >
            <svg
              className={`w-5 h-5 ${isFavorite ? "fill-current" : ""}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
              />
            </svg>
          </button>

          {/* Quick Actions Overlay - Absolute to image container */}
          <div className="absolute inset-x-0 bottom-0 p-2 sm:p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-500 bg-gradient-to-t from-black/80 via-black/40 to-transparent flex flex-col gap-2 sm:gap-3 z-30 opacity-0 group-hover:opacity-100">
            {/* Size Selection */}
            {product.sizes && product.sizes.length > 0 && (
              <div className="hidden sm:flex flex-wrap gap-1.5 justify-center">
                {product.sizes.slice(0, 5).map((size) => (
                  <button
                    key={size}
                    onClick={(e) => handleAddToCart(e, size)}
                    disabled={product.stock <= 0}
                    className={`w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center text-[10px] sm:text-[11px] font-bold rounded-lg transition-all shadow-sm ${
                      product.stock <= 0
                        ? "bg-gray-200 dark:bg-slate-700 text-gray-400 cursor-not-allowed opacity-50"
                        : "bg-white/90 dark:bg-slate-900/90 hover:bg-blue-600 dark:hover:bg-blue-500 hover:text-white text-gray-800 dark:text-slate-200"
                    }`}
                    title={product.stock <= 0 ? "Hết hàng" : `Thêm size ${size}`}
                  >
                    {size}
                  </button>
                ))}
              </div>
            )}

            <button
              onClick={handleQuickView}
              className="w-full bg-white dark:bg-slate-900 text-gray-900 dark:text-white py-2 sm:py-2.5 rounded-xl font-bold text-[10px] sm:text-xs hover:bg-blue-600 dark:hover:bg-blue-500 hover:text-white transition-colors shadow-xl flex items-center justify-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              <span>{product.stock <= 0 ? "Xem thông tin" : "Xem nhanh"}</span>
            </button>
          </div>
        </div>


        {/* Info Section */}
        <div className="p-4 flex flex-col flex-grow">
          <div className="flex justify-between items-start mb-1.5">
            <span className="text-[10px] sm:text-xs font-bold tracking-widest text-blue-600 dark:text-blue-400 uppercase truncate pr-2 h-4">
              {product.brand ? product.brand : <>TaHa <span className="text-red-600">Shoes</span></>}
            </span>
          </div>

          <Link to={`/product/${product.slug || productId}`} className="group/title">
            <h3 className="text-[13px] sm:text-[14px] font-bold leading-tight text-gray-800 dark:text-slate-200 hover:text-blue-600 dark:hover:text-blue-400 transition-colors mb-1.5 line-clamp-2 min-h-[32px] sm:min-h-[40px]">
              {product.name}
            </h3>
          </Link>

          {/* Rating & Sold count section */}
          <div className="flex items-center gap-2 mb-2">
            <div className="flex items-center gap-1 bg-yellow-50 dark:bg-yellow-900/20 px-1.5 py-0.5 rounded-md">
              <svg
                className="w-2.5 h-2.5 sm:w-3 h-3 text-yellow-500 fill-current"
                viewBox="0 0 20 20"
              >
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
              <span className="text-[10px] sm:text-xs font-bold text-yellow-700 dark:text-yellow-500">
                {product.rating || 5}
              </span>
            </div>
            <div className="w-[1px] h-3 bg-gray-200 dark:bg-slate-700"></div>
            <span className="text-[10px] sm:text-xs font-medium text-gray-500 dark:text-gray-400">
              Đã bán {product.soldCount > 1000 ? `${(product.soldCount / 1000).toFixed(1)}k` : (product.soldCount || 0)}
            </span>
          </div>

          <div className="mt-auto pt-2 sm:pt-3 border-t border-gray-50 dark:border-slate-800 flex items-center justify-between">
            <div className="flex flex-col min-h-[36px] sm:min-h-[44px] justify-center">
              <span className="text-blue-600 dark:text-blue-400 font-black text-base sm:text-lg leading-none">
                {formatPrice(product.price)}₫
              </span>
              {product.originalPrice ? (
                <span className="text-gray-400 dark:text-slate-500 text-[10px] sm:text-xs line-through mt-0.5 sm:mt-1">
                  {formatPrice(product.originalPrice)}₫
                </span>
              ) : (
                <div className="h-[14px] sm:h-[18px]"></div>
              )}
            </div>

            <button
              onClick={product.stock > 0 ? handleQuickView : undefined}
              disabled={product.stock <= 0}
              className={`flex w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl items-center justify-center transition-all ${
                product.stock <= 0
                  ? "bg-gray-100 dark:bg-slate-800 text-gray-300 cursor-not-allowed"
                  : "bg-red-600 text-white hover:bg-red-700 shadow-lg shadow-red-100 dark:shadow-none font-bold"
              }`}
            >
              <svg
                className="w-4 h-4 sm:w-5 sm:h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
            </button>
          </div>

        </div>
      </div>

      {showQuickView && (
        <ProductQuickView
          product={product}
          productId={productId}
          onClose={() => setShowQuickView(false)}
        />
      )}
    </>
  );
};

export default ProductCard;
